import { parseServerTimestamp } from "./traceUtils.js";

/** The newest-first prompt read both session-preview callers batch on. */
export function sessionPromptSpec({ ids, timeRange, notificationField, dimensions, perSessionLimit }) {
    return {
        root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
        timeRange,
        filter: {
            pred: {
                field: { stepAlias: "p", field: notificationField },
                op: "OPERATOR_IN",
                value: ids,
            },
        },
        output: { dimensions },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }],
        limit: ids.length * perSessionLimit,
    };
}

/**
 * Rows per session id, omitting any id the read could not answer.
 *
 * `hasAnswer(row)` marks a row the caller would pick for its session. When the
 * read fills its limit, a session holding such a row newer than the oldest row
 * returned is still answered: the read is newest-first, so every newer row of
 * that session is already in hand and a re-read would pick the same row. Only
 * the rest are re-read, so a quiet session with few prompts does not cost a
 * query of its own every time a busy one fills the shared budget.
 */
export async function groupSessionRows(runSpec, ids, buildSpec, { hasAnswer = null } = {}) {
    const spec = buildSpec(ids);
    const rows = await runSpec(spec);
    const grouped = new Map(ids.map((id) => [id, []]));
    for (const row of rows) {
        const group = grouped.get(String(row?.session_id ?? ""));
        if (group) group.push(row);
    }
    const perSession = spec.limit / ids.length;
    const full = rows.length >= spec.limit;
    // NaN when any timestamp is unparseable, which fails every comparison and
    // falls back to re-reading the short sessions.
    const cutoff = full ? Math.min(...rows.map((row) => parseServerTimestamp(row?.event_time))) : NaN;
    const settled = (sessionRows) => sessionRows.length >= perSession
        || (hasAnswer !== null && sessionRows.some(
            (row) => hasAnswer(row) && parseServerTimestamp(row.event_time) > cutoff,
        ));
    const starved = full
        ? [...grouped].filter(([, sessionRows]) => !settled(sessionRows)).map(([id]) => id)
        : [];
    const answered = new Map([...grouped].filter(([id]) => !starved.includes(id)));
    if (starved.length === 0 || ids.length === 1) return answered;
    const half = Math.ceil(starved.length / 2);
    const halves = [starved.slice(0, half), starved.slice(half)].filter((part) => part.length > 0);
    for (const recovered of await Promise.all(
        halves.map((part) => groupSessionRows(runSpec, part, buildSpec, { hasAnswer })),
    )) {
        for (const [id, sessionRows] of recovered) answered.set(id, sessionRows);
    }
    return answered;
}

const PROMPT_ROOT = { alias: "p", entity: "ENTITY_TYPE_PROMPT" };
const promptField = (field) => ({ stepAlias: "p", field });

// A filtered MAX over no matching rows comes back as the column default (the
// epoch) rather than null, so anything at or before it means "none".
function realTimestamp(value) {
    return parseServerTimestamp(value) > 0 ? String(value) : null;
}

/**
 * Rows at each session's newest prompt, and (given `summaryField`) at its newest
 * prompt with a non-empty summary, in two reads whose cost does not depend on
 * how busy any session is. A newest-first row read shares its limit across
 * sessions, so a few long sessions crowd out the rest and force re-reads.
 *
 * The first read groups by session for both timestamps; the second fetches
 * only the rows at them. `dimensions` must include `session_id` and
 * `event_time`. `runQuery` resolves to row objects and may throw. `root` is
 * the prompt index unless given another prompt entity (aliased "p", as the
 * fields are).
 *
 * Resolves to { rowsById, missing, incomplete }: `missing` sessions have no
 * prompt in the window; `incomplete` ones had rows the second read cut off.
 */
export async function readLatestSessionRows({
    runQuery,
    ids,
    timeRange,
    notificationField,
    timestampField,
    summaryField = null,
    dimensions,
    rowsPerSession,
    root = PROMPT_ROOT,
}) {
    const sessionIds = ids.map(String);
    const metrics = [{ outputAlias: "latest", computed: { field: promptField(timestampField), op: "AGGREGATE_OP_MAX" } }];
    if (summaryField) {
        metrics.push({
            outputAlias: "latest_summarized",
            computed: {
                field: promptField(timestampField),
                op: "AGGREGATE_OP_MAX",
                predicate: { pred: { field: promptField(summaryField), op: "OPERATOR_NE", value: "" } },
            },
        });
    }
    const latestRows = await runQuery({
        root,
        timeRange,
        filter: { pred: { field: promptField(notificationField), op: "OPERATOR_IN", value: sessionIds } },
        output: { dimensions: [{ field: promptField(notificationField), outputAlias: "session_id" }], metrics },
        limit: sessionIds.length,
    });
    const wanted = new Map();
    for (const row of latestRows) {
        const id = String(row?.session_id ?? "");
        const times = [realTimestamp(row.latest), summaryField ? realTimestamp(row.latest_summarized) : null].filter(Boolean);
        if (sessionIds.includes(id) && times.length) wanted.set(id, new Set(times));
    }
    const missing = sessionIds.filter((id) => !wanted.has(id));
    const rowsById = new Map();
    const incomplete = [];
    if (!wanted.size) return { rowsById, missing, incomplete };

    const readIds = [...wanted.keys()];
    const limit = readIds.length * rowsPerSession;
    const rows = await runQuery({
        root,
        timeRange,
        filter: { andExpr: { children: [
            { pred: { field: promptField(notificationField), op: "OPERATOR_IN", value: readIds } },
            { pred: { field: promptField(timestampField), op: "OPERATOR_IN", value: [...new Set([...wanted.values()].flatMap((t) => [...t]))] } },
        ] } },
        output: { dimensions },
        limit,
    });
    // Slack in `rowsPerSession` absorbs same-millisecond prompts and another
    // session's prompt that happens to share one of the chosen timestamps.
    const complete = rows.length < limit;
    const grouped = new Map(readIds.map((id) => [id, []]));
    for (const row of rows) {
        const id = String(row?.session_id ?? "");
        if (wanted.get(id)?.has(String(row.event_time))) grouped.get(id).push(row);
    }
    for (const [id, sessionRows] of grouped) {
        const found = new Set(sessionRows.map((row) => String(row.event_time)));
        if (complete || [...wanted.get(id)].every((time) => found.has(time))) rowsById.set(id, sessionRows);
        else incomplete.push(id);
    }
    return { rowsById, missing, incomplete };
}
